// Timeline PURE d'une animation : l'état exact de chaque objet à l'instant t
// d'une scène. Le lecteur, la capture vidéo, le mouvement réduit, les
// boutons précédent / suivant et les tests partent tous de ces fonctions.
//
//   compileAnimation(doc)        → objets résolus (nombres), état initial,
//                                  et pour chaque scène : calendrier,
//                                  segments, état de départ, état de fin ;
//   sceneStateAt(scene, t)       → état de chaque objet à l'instant t.
//
// Une scène part de l'état de fin de la précédente. Chaque étape devient un
// ou plusieurs SEGMENTS (une propriété d'un objet qui passe d'une valeur à
// une autre entre deux instants). Les segments sont triés par début ; à
// l'instant t, chaque propriété vaut celle du dernier segment commencé
// (interpolé s'il n'est pas fini). Les valeurs sont absolues : `object.move`
// déplace à (dx, dy) depuis la position d'origine, `object.rotate` tourne
// jusqu'à `angle`, `object.scale` agrandit jusqu'à `scale`.

import type { Scope } from '@jeraisonne/db/shared/expr';
import { buildScope, evalNum, objectBox, OBJECT_NUMERIC_FIELDS } from './expressions';
import { JRAF_LIMITS as L } from './limits';
import { resolveColor } from './palette';
import { scheduleScene, type SceneSchedule, type ScheduledStep } from './schedule';
import { glowAt, mentionHighlights, mergeHighlights, type Highlight } from './mentions';
import type { JrafDocument, JrafScene } from './schema/document';
import type { JrafObject } from './schema/objects';
import type { JrafEasing, NumExpr } from './schema/primitives';
import { stepTargets, type JrafStep } from './schema/steps';
import { narrationTimings, type NarrationTimings } from './voice';

// ------------------------------------------------------------ objets résolus

/** Champs numériques de chaque type (les autres restent tels quels). */
type NumKeys = {
  rect: 'x' | 'y' | 'width' | 'height' | 'rx';
  circle: 'cx' | 'cy' | 'r';
  ellipse: 'cx' | 'cy' | 'rx' | 'ry';
  line: 'x1' | 'y1' | 'x2' | 'y2';
  arrow: 'x1' | 'y1' | 'x2' | 'y2';
  polygon: never;
  path: never;
  point: 'x' | 'y';
  text: never;
  formula: never;
  value: 'expr';
  image: 'x' | 'y' | 'width' | 'height';
  group: 'x' | 'y';
};

export interface Point {
  x: number;
  y: number;
}

type ResolvedOf<T extends JrafObject> = {
  [K in keyof T]: K extends NumKeys[T['type']]
    ? undefined extends T[K]
      ? number | undefined
      : number
    : K extends 'at'
      ? Point
      : K extends 'points'
        ? Point[]
        : T[K];
};

/** Objet dont toutes les valeurs sont des nombres (paramètres appliqués). */
export type ResolvedObject = JrafObject extends infer T
  ? T extends JrafObject
    ? ResolvedOf<T>
    : never
  : never;

function resolvePoint(p: { x: NumExpr; y: NumExpr }, scope: Scope): Point {
  return { x: evalNum(p.x, scope), y: evalNum(p.y, scope) };
}

/** Remplace chaque expression d'un objet par sa valeur (document VALIDÉ). */
export function resolveObject(obj: JrafObject, scope: Scope): ResolvedObject {
  const out: Record<string, unknown> = { ...obj };
  for (const rel of OBJECT_NUMERIC_FIELDS[obj.type]) {
    if (rel.length !== 1) continue; // `at.x` / `at.y` : traités avec `at`
    const key = rel[0] as string;
    const v = (obj as Record<string, unknown>)[key] as NumExpr | undefined;
    if (v === undefined) continue;
    out[key] = evalNum(v, scope, key === 'expr' ? L.maxValueMagnitude : L.maxCoordinate);
  }
  if ('at' in obj) out.at = resolvePoint(obj.at, scope);
  if (obj.type === 'polygon') out.points = obj.points.map((p) => resolvePoint(p, scope));
  return out as ResolvedObject;
}

// ------------------------------------------------------------------- état

export interface ObjectState {
  /** apparition : 0 caché … opacité cible */
  opacity: number;
  /** tracé du contour (effet `draw`) : 0 … 1 */
  draw: number;
  /** facteur d'apparition (effet `pop`) */
  pop: number;
  dx: number;
  dy: number;
  scale: number;
  scaleOrigin: Point;
  rotate: number;
  rotateOrigin: Point;
  /** couleurs résolues (`#rrggbb` ou `none`) ; `null` : sans objet */
  fill: string | null;
  stroke: string | null;
  strokeWidth: number | null;
  styleOpacity: number;
  /** texte d'un `text`, étiquette d'un `point` */
  label: string | null;
  labelFrom: string | null;
  labelProgress: number;
  /** formule d'un `formula` (et la précédente pendant une transformation) */
  tex: string | null;
  texFrom: string | null;
  texProgress: number;
  /** nom du geste affiché pendant `formula.transform` */
  gesture: string | null;
  /** surbrillance d'une transformation : 0 … 1 */
  flash: number;
  /** éclairage (nommé par la voix, `object.highlight`) : 0 … 1 */
  glow: number;
  /** nombre d'un `value` */
  value: number | null;
}

export type AnimationState = Record<string, ObjectState>;

/** Styles par défaut : le rendu et la timeline partent des mêmes valeurs. */
export function defaultStyle(obj: JrafObject): {
  fill: string | null;
  stroke: string | null;
  strokeWidth: number | null;
} {
  const s = 'style' in obj ? obj.style : undefined;
  switch (obj.type) {
    case 'rect':
    case 'circle':
    case 'ellipse':
    case 'polygon':
    case 'path':
      return {
        fill: resolveColor(s?.fill ?? 'none'),
        stroke: resolveColor(s?.stroke ?? 'paper'),
        strokeWidth: s?.strokeWidth ?? 2,
      };
    case 'line':
    case 'arrow':
      return {
        fill: null,
        stroke: resolveColor(s?.stroke ?? 'paper'),
        strokeWidth: s?.strokeWidth ?? 3,
      };
    case 'point':
      return {
        fill: resolveColor(s?.fill ?? 'paper'),
        stroke: resolveColor(s?.stroke ?? 'none'),
        strokeWidth: s?.strokeWidth ?? 0,
      };
    case 'text':
    case 'formula':
    case 'value':
      // cadre `box` : sa bordure est le « stroke » du texte (couleur du texte par défaut)
      return {
        fill: resolveColor(obj.color ?? 'paper'),
        stroke: obj.box ? resolveColor(obj.box.stroke ?? obj.color ?? 'paper') : null,
        strokeWidth: obj.box ? obj.box.strokeWidth : null,
      };
    case 'image':
    case 'group':
      return { fill: null, stroke: null, strokeWidth: null };
  }
}

/** Opacité d'un objet visible (cible de `object.show`). */
export function shownOpacity(obj: JrafObject): number {
  return obj.initial?.opacity ?? 1;
}

/** Centre par défaut des agrandissements et rotations, dans le repère de l'objet. */
export function defaultOrigin(obj: ResolvedObject): Point {
  if (obj.type === 'group') return { x: obj.x, y: obj.y };
  const box = objectBox(obj as unknown as JrafObject, {});
  if (!box) return { x: 0, y: 0 };
  return { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
}

function initialState(obj: JrafObject, resolved: ResolvedObject): ObjectState {
  const style = defaultStyle(obj);
  const origin = defaultOrigin(resolved);
  return {
    opacity: obj.initial?.visible === false ? 0 : shownOpacity(obj),
    draw: 1,
    pop: 1,
    dx: 0,
    dy: 0,
    scale: 1,
    scaleOrigin: origin,
    rotate: 0,
    rotateOrigin: origin,
    ...style,
    styleOpacity: 'style' in obj ? (obj.style?.opacity ?? 1) : 1,
    label: obj.type === 'text' ? obj.text : obj.type === 'point' ? (obj.label ?? null) : null,
    labelFrom: null,
    labelProgress: 1,
    tex: obj.type === 'formula' ? obj.tex : null,
    texFrom: null,
    texProgress: 1,
    gesture: null,
    flash: 0,
    glow: 0,
    value: resolved.type === 'value' ? resolved.expr : null,
  };
}

function cloneState(s: AnimationState): AnimationState {
  const out: AnimationState = {};
  for (const [id, o] of Object.entries(s)) out[id] = { ...o };
  return out;
}

// ---------------------------------------------------------------- easing

export type TimelineEasing = JrafEasing | 'outBack';

export function ease(kind: TimelineEasing, p: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  const t = p;
  switch (kind) {
    case 'linear':
      return t;
    case 'easeIn':
      return t * t * t;
    case 'easeOut':
      return 1 - (1 - t) ** 3;
    case 'easeInOut':
      return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
    case 'outBack': {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
    }
  }
}

// -------------------------------------------------------------- segments

export type NumProp =
  | 'opacity'
  | 'draw'
  | 'pop'
  | 'dx'
  | 'dy'
  | 'scale'
  | 'rotate'
  | 'strokeWidth'
  | 'styleOpacity'
  | 'value';

interface SegmentBase {
  objectId: string;
  /** index de l'étape dans la scène */
  stepIndex: number;
  start: number;
  end: number;
}

export type Segment =
  | (SegmentBase & {
      kind: 'num';
      prop: NumProp;
      from: number;
      to: number;
      easing: TimelineEasing;
      origin?: Point;
    })
  | (SegmentBase & {
      kind: 'color';
      prop: 'fill' | 'stroke';
      from: string | null;
      to: string;
      easing: TimelineEasing;
    })
  | (SegmentBase & { kind: 'label'; from: string | null; to: string })
  | (SegmentBase & {
      kind: 'formula';
      from: string | null;
      to: string;
      gesture: string | null;
      flash: boolean;
    });

export interface CompiledScene {
  index: number;
  scene: JrafScene;
  schedule: SceneSchedule;
  /** début de chaque phrase sur l'horloge de la scène (étiré : la voix attend l'image) */
  sentenceStartsMs: number[];
  /** début de chaque phrase dans la voix (fichier enregistré, ou estimation sans pauses de lecture) */
  voiceStartsMs: number[];
  /** voix enregistrée : début utile dans le fichier (silence rogné) ; 0 sinon */
  voiceOffsetMs: number;
  /** durée de la voix (utile, depuis `voiceOffsetMs`) */
  voiceTotalMs: number;
  /** segments triés par début (puis par ordre d'écriture) */
  segments: Segment[];
  /** éclairages (objet nommé par la voix, `object.highlight`), triés par début */
  highlights: Highlight[];
  start: AnimationState;
  end: AnimationState;
}

export interface CompiledAnimation {
  doc: JrafDocument;
  scope: Scope;
  /** objets résolus, dans l'ordre du document (ordre de dessin) */
  objects: ResolvedObject[];
  initial: AnimationState;
  scenes: CompiledScene[];
}

export interface CompileOptions {
  /** valeurs des paramètres (défaut : valeurs par défaut du document) */
  parameterValues?: Record<string, number>;
  /** vitesse de la voix du navigateur (estimation des phrases) */
  rate?: number;
}

/**
 * Débuts des phrases et durée de la narration d'une scène : estimés (voix du
 * navigateur) ou ceux de la voix enregistrée (calés sur ses mots, silences
 * du début et de la fin rognés : `offsetMs` dans le fichier).
 */
export function sceneNarrationTimings(
  doc: Pick<JrafDocument, 'assets'>,
  scene: Pick<JrafScene, 'narration'>,
  rate = 1,
): NarrationTimings {
  const id = scene.narration.audioAssetId;
  const audio = id ? doc.assets.find((a) => a.id === id) : undefined;
  return narrationTimings(scene.narration, audio?.kind === 'audio' ? audio : null, rate);
}

/** Valeur d'un curseur : bornée et posée sur son pas (défaut si absente). */
export function snapToSlider(
  p: { default: number; min: number; max: number; step?: number | undefined },
  v: number | undefined,
): number {
  if (v === undefined || !Number.isFinite(v)) return p.default;
  let x = Math.min(p.max, Math.max(p.min, v));
  if (p.step !== undefined && p.step > 0) {
    x = p.min + Math.round((x - p.min) / p.step) * p.step;
    x = Math.min(p.max, Math.round(x * 1e9) / 1e9);
  }
  return x;
}

/** Évalue avec les paramètres choisis ; à défaut (valeur non calculable), avec les valeurs par défaut. */
type SafeEval = <T>(f: (scope: Scope) => T) => T;

export function compileAnimation(doc: JrafDocument, opts: CompileOptions = {}): CompiledAnimation {
  const values = opts.parameterValues ?? {};
  const defaults = buildScope(doc.parameters, 'default').scope;
  const { scope } = buildScope(doc.parameters, (p) => snapToSlider(p, values[p.id]));
  // La validation teste un échantillon des valeurs atteignables : une valeur
  // non calculable pour un réglage précis retombe sur celle des paramètres
  // par défaut (toujours calculable : document validé) plutôt que de casser.
  const safe: SafeEval = (f) => {
    try {
      return f(scope);
    } catch {
      return f(defaults);
    }
  };
  const objects = doc.objects.map((o) => safe((s) => resolveObject(o, s)));
  const byId = new Map(doc.objects.map((o, i) => [o.id, { raw: o, resolved: objects[i]! }]));
  const initial: AnimationState = {};
  for (const { raw, resolved } of byId.values()) initial[raw.id] = initialState(raw, resolved);

  const scenes: CompiledScene[] = [];
  let state = initial;
  doc.scenes.forEach((scene, index) => {
    const timings = sceneNarrationTimings(doc, scene, opts.rate);
    const schedule = scheduleScene(scene, {
      sentenceStartsMs: timings.startsMs,
      narrationMs: timings.totalMs,
    });
    const { segments, end } = buildSegments(scene, schedule, state, byId, safe);
    const highlights = mergeHighlights([
      ...stepHighlights(scene, schedule),
      ...mentionHighlights(
        scene,
        doc.objects,
        schedule.sentenceStartsMs,
        timings.startsMs,
        timings.totalMs,
      ),
    ]);
    scenes.push({
      index,
      scene,
      schedule,
      sentenceStartsMs: schedule.sentenceStartsMs,
      voiceStartsMs: timings.startsMs,
      voiceOffsetMs: timings.offsetMs,
      voiceTotalMs: timings.totalMs,
      segments,
      highlights,
      start: state,
      end,
    });
    state = end;
  });
  return { doc, scope, objects, initial, scenes };
}

type ObjectIndex = Map<string, { raw: JrafObject; resolved: ResolvedObject }>;

/** Éclairages des étapes `object.highlight`. */
function stepHighlights(scene: JrafScene, schedule: SceneSchedule): Highlight[] {
  const out: Highlight[] = [];
  for (const sched of schedule.steps) {
    const step = scene.steps[sched.index]!;
    if (step.action !== 'object.highlight') continue;
    for (const id of stepTargets(step))
      out.push({ objectId: id, start: sched.startMs, end: sched.endMs, source: 'step' });
  }
  return out;
}

const DEFAULT_EASING: Partial<Record<JrafStep['action'], TimelineEasing>> = {
  'object.show': 'easeOut',
  'object.hide': 'easeIn',
  'object.move': 'easeInOut',
  'object.scale': 'easeInOut',
  'object.rotate': 'easeInOut',
  'object.style': 'easeInOut',
  'value.set': 'easeOut',
};

function buildSegments(
  scene: JrafScene,
  schedule: SceneSchedule,
  start: AnimationState,
  objects: ObjectIndex,
  safe: SafeEval,
): { segments: Segment[]; end: AnimationState } {
  const segments: Segment[] = [];
  const order = [...schedule.steps].sort((a, b) => a.startMs - b.startMs || a.index - b.index);

  for (const sched of order) {
    const step = scene.steps[sched.index]!;
    const easing: TimelineEasing =
      ('easing' in step ? step.easing : undefined) ?? DEFAULT_EASING[step.action] ?? 'linear';
    // valeurs de départ = l'état À CET INSTANT (une étape qui en chevauche
    // une autre sur la même propriété part de la valeur courante, sans saut)
    const at = sceneStateAt({ start, segments }, sched.startMs);
    const created: Segment[] = [];
    for (const id of stepTargets(step)) {
      const o = objects.get(id);
      const s = at[id];
      if (!o || !s) continue; // document validé : n'arrive pas
      created.push(...stepSegments(step, sched, o, s, easing, safe));
    }
    segments.push(...created);
  }
  // déjà triés par début (étapes parcourues dans l'ordre de leurs débuts)
  segments.sort((a, b) => a.start - b.start || a.stepIndex - b.stepIndex);
  return { segments, end: sceneStateAt({ start, segments }, Number.POSITIVE_INFINITY) };
}

function stepSegments(
  step: JrafStep,
  sched: ScheduledStep,
  o: { raw: JrafObject; resolved: ResolvedObject },
  s: ObjectState,
  easing: TimelineEasing,
  safe: SafeEval,
): Segment[] {
  const base = { objectId: o.raw.id, stepIndex: sched.index, start: sched.startMs };
  const span = { ...base, end: sched.endMs };
  const num = (
    prop: NumProp,
    to: number,
    e: TimelineEasing = easing,
    end = sched.endMs,
    origin?: Point,
    from: number = s[prop] ?? 0,
  ): Segment => ({
    ...base,
    end,
    kind: 'num',
    prop,
    from,
    to,
    easing: e,
    ...(origin ? { origin } : {}),
  });
  const d = sched.endMs - sched.startMs;

  switch (step.action) {
    case 'object.show': {
      const target = shownOpacity(o.raw);
      if (step.effect === 'none') return [num('opacity', target, 'linear', sched.startMs)];
      if (step.effect === 'draw')
        return [
          num('draw', 1, 'linear', sched.endMs, undefined, 0),
          num('opacity', target, 'linear', sched.startMs + Math.min(150, d)),
        ];
      if (step.effect === 'pop')
        return [
          num('opacity', target, 'easeOut', sched.startMs + Math.round(d * 0.4)),
          num('pop', 1, 'outBack', sched.endMs, undefined, 0.4),
        ];
      return [num('opacity', target)];
    }
    case 'object.hide':
      return step.effect === 'none'
        ? [num('opacity', 0, 'linear', sched.startMs)]
        : [num('opacity', 0)];
    case 'object.move':
      return [
        num(
          'dx',
          safe((sc) => evalNum(step.dx, sc, L.maxCoordinate)),
        ),
        num(
          'dy',
          safe((sc) => evalNum(step.dy, sc, L.maxCoordinate)),
        ),
      ];
    case 'object.scale':
      return [
        num(
          'scale',
          step.scale,
          easing,
          sched.endMs,
          step.origin ? safe((sc) => resolvePoint(step.origin!, sc)) : s.scaleOrigin,
        ),
      ];
    case 'object.rotate':
      return [
        num(
          'rotate',
          step.angle,
          easing,
          sched.endMs,
          step.origin ? safe((sc) => resolvePoint(step.origin!, sc)) : s.rotateOrigin,
        ),
      ];
    case 'object.style': {
      const out: Segment[] = [];
      if (step.fill !== undefined)
        out.push({
          ...span,
          kind: 'color',
          prop: 'fill',
          from: s.fill,
          to: resolveColor(step.fill),
          easing,
        });
      if (step.stroke !== undefined)
        out.push({
          ...span,
          kind: 'color',
          prop: 'stroke',
          from: s.stroke,
          to: resolveColor(step.stroke),
          easing,
        });
      if (step.strokeWidth !== undefined) out.push(num('strokeWidth', step.strokeWidth));
      if (step.opacity !== undefined) out.push(num('styleOpacity', step.opacity));
      return out;
    }
    case 'label.set':
      return [{ ...span, kind: 'label', from: s.label, to: step.text }];
    case 'formula.transform':
      return [
        {
          ...span,
          kind: 'formula',
          from: s.tex,
          to: step.to,
          gesture: step.label ?? null,
          flash: step.flash,
        },
      ];
    case 'value.set':
      return [
        num(
          'value',
          safe((sc) => evalNum(step.expr, sc, L.maxValueMagnitude)),
        ),
      ];
    case 'object.highlight': // éclairage passager : `highlights`, pas un segment
    case 'timeline.wait':
    case 'question.ask':
    case 'tool.open':
      return [];
  }
}

function mixColor(from: string | null, to: string, p: number): string {
  if (p >= 1) return to;
  if (!from || !/^#[0-9a-f]{6}$/i.test(from) || !/^#[0-9a-f]{6}$/i.test(to))
    return p > 0 ? to : (from ?? to);
  const a = parseInt(from.slice(1), 16);
  const b = parseInt(to.slice(1), 16);
  const ch = (shift: number) => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(x + (y - x) * p);
  };
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
}

/** Applique un segment à l'avancement brut `p` (0 … 1, avant easing). */
function applySegment(s: ObjectState, seg: Segment, p: number): void {
  switch (seg.kind) {
    case 'num': {
      const e = ease(seg.easing, p);
      s[seg.prop] = seg.from + (seg.to - seg.from) * e;
      if (seg.origin && seg.prop === 'scale') s.scaleOrigin = seg.origin;
      if (seg.origin && seg.prop === 'rotate') s.rotateOrigin = seg.origin;
      return;
    }
    case 'color':
      s[seg.prop] = mixColor(seg.from, seg.to, ease(seg.easing, p));
      return;
    case 'label':
      s.label = seg.to;
      s.labelFrom = p >= 1 ? null : seg.from;
      s.labelProgress = p;
      return;
    case 'formula':
      s.tex = seg.to;
      s.texFrom = p >= 1 ? null : seg.from;
      s.texProgress = p;
      s.gesture = p >= 1 ? null : seg.gesture;
      s.flash = seg.flash && p < 1 ? Math.sin(Math.PI * p) : 0;
      return;
  }
}

export interface StateAtOptions {
  /** mouvement réduit : chaque étape commencée est à son état de fin */
  reducedMotion?: boolean;
}

/** État de chaque objet à l'instant `t` (ms) de la scène. */
export function sceneStateAt(
  scene: Pick<CompiledScene, 'start' | 'segments'> & Partial<Pick<CompiledScene, 'highlights'>>,
  t: number,
  opts: StateAtOptions = {},
): AnimationState {
  const s = cloneState(scene.start);
  for (const seg of scene.segments) {
    if (seg.start > t) break; // triés par début
    const obj = s[seg.objectId];
    if (!obj) continue;
    const p =
      opts.reducedMotion || seg.end <= seg.start
        ? 1
        : Math.min(1, Math.max(0, (t - seg.start) / (seg.end - seg.start)));
    applySegment(obj, seg, p);
    // mouvement réduit : la formule change d'un coup, le nom du geste reste lisible
    if (opts.reducedMotion && seg.kind === 'formula' && t < seg.end) obj.gesture = seg.gesture;
  }
  // éclairages passagers (jamais dans l'état de fin : ils sont éteints)
  for (const h of scene.highlights ?? []) {
    if (h.start > t) break; // triés par début
    const obj = s[h.objectId];
    if (obj) obj.glow = Math.max(obj.glow, glowAt(h, t, opts.reducedMotion));
  }
  return s;
}

/** Ordre des étapes interactives (portes) d'une scène, par début. */
export function sceneGates(scene: Pick<CompiledScene, 'schedule'>): ScheduledStep[] {
  return scene.schedule.steps.filter((s) => s.gate).sort((a, b) => a.startMs - b.startMs);
}
